import { useEffect, useMemo, useState } from 'react';
import { Search, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MotionConfig, motion } from 'framer-motion';
import CategoryFilter, {
  PRACTICE_TABS,
  type FilterCounts,
  type PracticeFilter,
} from '@/components/practice/CategoryFilter';
import QuestionRow from '@/components/practice/QuestionRow';
import type { AnswerState } from '@/components/practice/PracticeQuestionCard';
import { fetchAllQuestions } from '@/lib/api/questions';
import { gradeAnswer } from '@/lib/api/interview';
import { getPracticeError } from '@/lib/errors/practiceErrors';
import { useSubscription } from '@/contexts/useSubscription';
import { logger } from '@/lib/logger';
import { captureFriendlyError } from '@/lib/sentry';
import { track, mapPracticeCategory } from '@/lib/analytics';
import type { Question } from '@/types/database';

const RISE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
};

function buildCounts(questions: Question[]): FilterCounts {
  const counts: FilterCounts = {
    all: 0,
    government: 0,
    history: 0,
    civics: 0,
  };
  for (const q of questions) {
    for (const tab of PRACTICE_TABS) {
      if (tab.matches(q.category ?? '')) counts[tab.value] += 1;
    }
  }
  return counts;
}

function applyFilters(
  questions: Question[],
  filter: PracticeFilter,
  query: string,
): Question[] {
  const tab = PRACTICE_TABS.find((t) => t.value === filter);
  const trimmed = query.trim().toLowerCase();
  return questions.filter((q) => {
    if (tab && !tab.matches(q.category ?? '')) return false;
    if (!trimmed) return true;
    return (
      q.question_text.toLowerCase().includes(trimmed) ||
      (q.category ?? '').toLowerCase().includes(trimmed) ||
      String(q.id) === trimmed
    );
  });
}

export default function PracticePage() {
  const { isPremium, isLoading: subLoading } = useSubscription();

  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [filter, setFilter] = useState<PracticeFilter>('all');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('voice');
  const [answerState, setAnswerState] = useState<AnswerState>({ kind: 'idle' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAllQuestions();
        if (cancelled) return;
        const sorted = [...data].sort((a, b) => a.id - b.id);
        setAllQuestions(sorted);
      } finally {
        if (!cancelled) setQuestionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => buildCounts(allQuestions), [allQuestions]);
  const filtered = useMemo(
    () => applyFilters(allQuestions, filter, query),
    [allQuestions, filter, query],
  );

  const freeQuestionId = filtered[0]?.id ?? null;
  const totalCount = allQuestions.length;

  const handleFilterChange = (next: PracticeFilter) => {
    setFilter(next);
    setExpandedId(null);
    setAnswerState({ kind: 'idle' });
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setExpandedId(null);
    setAnswerState({ kind: 'idle' });
  };

  const toggleExpanded = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
    setAnswerState({ kind: 'idle' });
  };

  const handleAnswer = async (text: string) => {
    if (expandedId === null) return;
    const current = allQuestions.find((q) => q.id === expandedId);
    if (!current) return;
    setAnswerState({ kind: 'grading', userAnswer: text });
    const result = await gradeAnswer(current.id, text);
    if (!result.success || result.isCorrect === undefined) {
      const code = result.error?.code ?? 'grading_failed';
      const friendly = getPracticeError(code);
      const context = { questionId: current.id, apiCode: code };
      logger.error('Practice grading failed', result.error?.raw, context);
      captureFriendlyError(result.error?.raw, friendly, context);
      setAnswerState({
        kind: 'grade_error',
        userAnswer: text,
        error: friendly,
        correctAnswer: current.answer_config?.accepted_answers?.join(' · '),
      });
      return;
    }
    setAnswerState({
      kind: 'graded',
      userAnswer: text,
      isCorrect: result.isCorrect,
      correctAnswer: result.correctAnswer,
    });
    track('practice_answer_submitted', {
      question_id: String(current.id),
      category: mapPracticeCategory(current.category),
      input_mode: inputMode,
      is_correct: result.isCorrect,
    });
  };

  const handleReveal = () => setAnswerState({ kind: 'revealed' });
  const handleError = () => setAnswerState({ kind: 'idle' });

  const isLoading = questionsLoading || subLoading;

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-cream">
        <main className="mx-auto w-full max-w-3xl px-6 py-8 md:py-12">
          <motion.header className="mb-5" {...RISE}>
            <h1 className="font-display italic font-light text-3xl md:text-4xl text-text-primary mb-2">
              Practice
            </h1>
            <p className="font-sans text-sm md:text-base text-text-secondary">
              All 128 USCIS civics questions, in one place.
            </p>
          </motion.header>

          {!isLoading && (
            <motion.div
              className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]"
              initial={RISE.initial}
              animate={RISE.animate}
              transition={{ ...RISE.transition, delay: 0.08 }}
            >
              {isPremium ? (
                <>
                  <span className="font-sans font-semibold uppercase tracking-[0.18em] text-primary">
                    All unlocked
                  </span>
                  <span className="text-text-muted/60">·</span>
                  <span className="font-sans text-text-muted">
                    {totalCount} questions ready to practice
                  </span>
                </>
              ) : (
                <>
                  <span className="font-sans font-semibold uppercase tracking-[0.18em] text-primary">
                    Free preview
                  </span>
                  <span className="text-text-muted/60">·</span>
                  <span className="font-sans text-text-muted">
                    1 of {totalCount} unlocked
                  </span>
                  <span className="text-text-muted/60">·</span>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1 font-sans font-semibold text-primary hover:text-primary-dark transition-colors group"
                  >
                    Upgrade for all {totalCount}
                    <ArrowRight
                      size={12}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                </>
              )}
            </motion.div>
          )}

          <div className="space-y-3">
            <motion.div
              className="relative"
              initial={RISE.initial}
              animate={RISE.animate}
              transition={{ ...RISE.transition, delay: 0.14 }}
            >
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
                placeholder="Find a question…"
                disabled={isLoading}
                className="w-full rounded-xl border border-border bg-card pl-11 pr-10 py-3 font-sans text-sm md:text-base text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors disabled:opacity-50"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => handleQueryChange('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-full text-text-muted hover:text-text-primary hover:bg-cream cursor-pointer"
                >
                  <X size={16} />
                </button>
              )}
            </motion.div>

            <motion.div
              initial={RISE.initial}
              animate={RISE.animate}
              transition={{ ...RISE.transition, delay: 0.2 }}
            >
              <CategoryFilter
                filter={filter}
                counts={counts}
                onChange={handleFilterChange}
                disabled={isLoading}
              />
            </motion.div>
          </div>

          <div className="mt-6 space-y-3">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-border bg-card p-5 animate-pulse"
                >
                  <div className="h-3 w-20 bg-border/60 rounded mb-2" />
                  <div className="h-4 w-3/4 bg-border/60 rounded" />
                </div>
              ))
            ) : filtered.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-10 text-center">
                <p className="font-sans text-text-secondary">
                  No questions match your search.
                </p>
              </div>
            ) : (
              filtered.map((q, idx) => {
                const isExpanded = expandedId === q.id;
                const isLocked = !isPremium && q.id !== freeQuestionId;
                const delay = Math.min(0.26 + idx * 0.025, 0.6);
                const nextAccessible = filtered
                  .slice(idx + 1)
                  .find((nq) => isPremium || nq.id === freeQuestionId);
                const nextId = nextAccessible?.id ?? null;
                return (
                  <motion.div
                    key={q.id}
                    initial={RISE.initial}
                    animate={RISE.animate}
                    transition={{ ...RISE.transition, delay }}
                  >
                    <QuestionRow
                      question={q}
                      isLocked={isLocked}
                      isExpanded={isExpanded}
                      onToggle={() => toggleExpanded(q.id)}
                      inputMode={inputMode}
                      onInputModeChange={setInputMode}
                      answerState={isExpanded ? answerState : { kind: 'idle' }}
                      onAnswer={handleAnswer}
                      onError={handleError}
                      onReveal={handleReveal}
                      onNext={() => nextId !== null && toggleExpanded(nextId)}
                      hasNext={nextId !== null}
                    />
                  </motion.div>
                );
              })
            )}
          </div>
        </main>
      </div>
    </MotionConfig>
  );
}
